import { AsyncPipe, NgClass } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output
} from "@angular/core";
import { groupBy, orderBy, take, uniqBy } from "lodash";
import { Observable, Subject, isObservable, of } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { HeaderComponent } from "../header/header.component";
import { MessagePanelItemComponent } from "./message-panel-item/message-panel-item.component";
import { IMessageLabel, IMessageModel, MessageSeverity } from "./message-panel.models";

@Component({
    selector: "rask-message-panel",
    templateUrl: "./message-panel.component.html",
    styleUrls: ["./message-panel.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        HeaderComponent,
        MessagePanelItemComponent,
        NgClass,
        AsyncPipe
    ]
})
export class MessagePanelComponent implements OnInit, OnDestroy {

    @Input() numberOfItemsToDisplay = 5;
    @Input() maxNumOfRows = 2;
    @Input() labels: IMessageLabel[];
    @Input() items: IMessageModel[] | Observable<IMessageModel[]>;
    @Output() anchorClick = new EventEmitter<IMessageModel>();

    items$: Observable<IMessageModel[]>;
    messages: Observable<IMessageViewModel[]>;
    expanded = false;

    private originalMessages: IMessageModel[];
    private messageLabels: IMessageLabel[];
    private remainder: number;

    private readonly destroy$ = new Subject<void>();

    get toggleText(): string {
        return this.expanded
            ? "Visa mindre"
            : `Visa ytterligare ${this.remainder} meddelanden`;
    }

    get toggleEnabled(): boolean {
        return this.remainder && this.remainder > 0;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnInit(): void {
        const defaultLabels: IMessageLabel[] = [
            {
                severity: MessageSeverity.Critical,
                labelText: "Allvarligt fel"
            },
            {
                severity: MessageSeverity.Error,
                labelText: "Standardfel"
            },
            {
                severity: MessageSeverity.Warning,
                labelText: "Varning"
            },
            {
                severity: MessageSeverity.Update,
                labelText: "Uppdatering krävs"
            },
            {
                severity: MessageSeverity.Info,
                labelText: "Information"
            }
        ];
        this.messageLabels = uniqBy([
            ...(this.labels || []),
            ...defaultLabels
        ], x => x.severity);

        if (!ObjectUtils.isNullOrUndefined(this.items)) {
            if (isObservable(this.items)) {
                this.items$ = this.items;
            } else {
                this.items$ = of(this.items);
            }

            this.items$.pipe(
                takeUntil(this.destroy$)
            ).subscribe((x) => {
                this.originalMessages = x;
                if (this.originalMessages?.length <= this.numberOfItemsToDisplay) {
                    this.expanded = false;
                }
                this.render();
            });
        }
    }

    toggle(): void {
        this.expanded = !this.expanded;
        this.render();
    }

    anchorClicked = (item: IMessageModel) => {
        this.anchorClick.emit(item);
    };

    private render(): void {
        let orderedMessages = orderBy(this.originalMessages,
            [
                x => x.severity,
                x => x.priority
            ],
            [
                "desc",
                "asc"
            ]);

        if (!this.expanded) {
            orderedMessages = take(orderedMessages, this.numberOfItemsToDisplay);
            this.remainder = this.originalMessages.length - orderedMessages.length;
        }

        const groups = groupBy(orderedMessages, x => x.severity);

        const viewModel: IMessageViewModel[] = [];
        for (const key in groups) {
            if (Object.hasOwn(groups, key)) {
                viewModel.push({
                    label: this.messageLabels.find(x => x.severity === +key).labelText,
                    messages: groups[key]
                });
            }
        }

        this.messages = of(viewModel);
    }
}

interface IMessageViewModel {
    label: string;
    messages: IMessageModel[];
}
